import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, X, Info, Filter, Check, ArrowUpDown, ArrowUp, ArrowDown, CheckSquare, Square, ChevronLeft, ChevronRight, UserCheck, ArrowLeft, Eye, Zap, Download, Settings, Search, ArrowLeftRight, StickyNote, Route as RouteIcon } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import axios from "@/lib/axios";
import VerificationDebug from "@/pages/VerificationDebug";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { UserDetailModal } from '@/components/UserDetailModal';
import { LeadsByPhoneModal } from '@/components/LeadsByPhoneModal';
import { CRMLeadNotesModal } from '@/components/CRMLeadNotesModal';

const filterGlowStyles = `
  @keyframes filter-glow {
    0% {
      box-shadow: 0 0 5px rgba(34, 197, 94, 0.2);
      border-color: rgba(34, 197, 94, 0.4);
    }
    50% {
      box-shadow: 0 0 15px rgba(34, 197, 94, 0.6), 0 0 5px rgba(34, 197, 94, 0.4);
      border-color: rgba(34, 197, 94, 0.8);
      transform: scale(1.02);
    }
    100% {
      box-shadow: 0 0 5px rgba(34, 197, 94, 0.2);
      border-color: rgba(34, 197, 94, 0.4);
    }
  }

  @keyframes filter-blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.8; }
  }

  .filter-active-glow {
    animation: filter-glow 2s infinite ease-in-out, filter-blink 2s infinite ease-in-out;
    background-color: rgba(34, 197, 94, 0.05) !important;
    border-width: 1.5px !important;
    position: relative;
    z-index: 1;
  }
`;

const layoutStyles = `
  /* Compact Layout */
  .table-layout-compact th { padding-top: 4px !important; padding-bottom: 4px !important; font-size: 11px !important; }
  .table-layout-compact td { padding-top: 4px !important; padding-bottom: 4px !important; font-size: 11px !important; }
  .table-layout-compact td span { font-size: 11px !important; }
  .table-layout-compact td div[class*="text-xs"] { font-size: 10px !important; }
  .table-layout-compact td div[class*="text-[10px]"] { font-size: 9px !important; }
  .table-layout-compact tr { height: 32px !important; }
  .table-layout-compact button { height: 24px !important; width: 24px !important; padding: 0 !important; }
  .table-layout-compact button svg { height: 12px !important; width: 12px !important; }
  
  /* Standard Layout */
  .table-layout-standard th { padding-top: 8px !important; padding-bottom: 8px !important; font-size: 13px !important; }
  .table-layout-standard td { padding-top: 4px !important; padding-bottom: 4px !important; font-size: 13px !important; }
  .table-layout-standard tr { height: 40px !important; }

  /* Spacious Layout */
  .table-layout-spacious th { padding-top: 14px !important; padding-bottom: 14px !important; font-size: 14px !important; }
  .table-layout-spacious td { padding-top: 12px !important; padding-bottom: 12px !important; font-size: 14px !important; }
  .table-layout-spacious td span { font-size: 14px !important; }
  .table-layout-spacious td div[class*="text-xs"] { font-size: 13px !important; }
  .table-layout-spacious td div[class*="text-[10px]"] { font-size: 12px !important; }
  .table-layout-spacious tr { height: 56px !important; }
  .table-layout-spacious button { height: 36px !important; width: 36px !important; }
  .table-layout-spacious button svg { height: 18px !important; width: 18px !important; }
`;

interface User {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
    phone_number?: string | null;
    phone_from_individual?: boolean;
    is_staff: boolean;
    is_superuser: boolean;
    business_name: string | null;
    bap_name: string | null;
    is_whitespace: boolean | null;
    crm_lead_id?: number | null;
    data_source: string | null;
    journey_stage: string;
    journey_stage_name: string;
    journey_index: string;
    journey_updated_at: string | null;
}

interface JourneyAnalysisProps {
    externalSettings?: {
        timezone: string;
        layout: 'compact' | 'standard' | 'spacious';
    };
    onSettingsChange?: (newSettings: { timezone: string; layout: 'compact' | 'standard' | 'spacious' }) => void;
    // @origin: v2 | @since: 3d9ebf6d | @used-by: ALL | @purpose: 2f port --
    // navigates to JourneyUrlTimeAnalysis.tsx for the clicked user.
    onOpenUrlAnalysis?: (user: User) => void;
}

const GreenCheck = () => (
    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-50 border border-green-100 shadow-sm">
        <Check className="h-3.5 w-3.5 text-green-500 stroke-[3px]" />
    </div>
);

const RedX = () => (
    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-red-50 border border-red-100 shadow-sm">
        <X className="h-3.5 w-3.5 text-red-500 stroke-[3px]" />
    </div>
);


const formatDate = (dateString: string | null, offset: string = "+05:30") => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";

    // Parse offset like "+05:30" or "-07:00"
    const sign = offset.startsWith("-") ? -1 : 1;
    const [hours, minutes] = offset.substring(1).split(":").map(Number);
    const totalOffsetMinutes = sign * (hours * 60 + minutes);

    // Apply offset (UTC + offset)
    const utcTime = date.getTime() + (date.getTimezoneOffset() * 60000);
    const adjustedDate = new Date(utcTime + (totalOffsetMinutes * 60000));

    return adjustedDate.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    }).replace(',', '');
};

interface PaginationControlsProps {
    currentPage: number;
    itemsPerPage: number;
    totalPages: number;
    totalItems: number;
    filteredCount: number;
    startIndex: number;
    pageSizeOptions: number[];
    onItemsPerPageChange: (value: string) => void;
    onPageChange: (page: number) => void;
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
    currentPage,
    itemsPerPage,
    totalPages,
    totalItems,
    filteredCount,
    startIndex,
    pageSizeOptions,
    onItemsPerPageChange,
    onPageChange,
}) => (
    <div className="flex flex-row flex-wrap items-center justify-between gap-4 py-1 w-full px-4 pb-4">
        <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
                <span className="text-[13px] text-gray-500 font-medium whitespace-nowrap">Show per page</span>
                <Select value={String(itemsPerPage)} onValueChange={onItemsPerPageChange}>
                    <SelectTrigger className="w-[75px] h-8 text-xs border-intellend-green/20 focus:ring-intellend-green/20">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {pageSizeOptions.map(option => (
                            <SelectItem key={option} value={String(option)} className="text-xs">
                                {option}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="h-1 w-1 bg-gray-300 rounded-full hidden sm:block" />
            <span className="text-[13px] text-gray-500 font-medium">
                Showing {filteredCount > 0 ? startIndex + 1 : 0}-{Math.min(startIndex + itemsPerPage, filteredCount)} of {filteredCount}
                {filteredCount !== totalItems && <span className="text-intellend-green/70 ml-1"> (filtered)</span>}
            </span>
        </div>

        {totalPages > 1 && (
            <Pagination className="justify-center lg:justify-end w-auto mx-0">
                <PaginationContent className="gap-2">
                    <PaginationItem>
                        <PaginationPrevious 
                            onClick={() => onPageChange(currentPage - 1)}
                            className={`h-8 w-auto px-3 cursor-pointer ${currentPage === 1 ? 'pointer-events-none opacity-50' : 'hover:bg-intellend-green/5'}`}
                        />
                    </PaginationItem>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                        if (
                            page === 1 || 
                            page === totalPages || 
                            (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                            return (
                                <PaginationItem key={page}>
                                    <PaginationLink 
                                        onClick={() => onPageChange(page)}
                                        isActive={currentPage === page}
                                        className={`h-8 w-8 text-xs cursor-pointer ${currentPage === page ? 'bg-intellend-green text-white hover:bg-intellend-green hover:text-white border-intellend-green' : 'hover:bg-intellend-green/5'}`}
                                    >
                                        {page}
                                    </PaginationLink>
                                </PaginationItem>
                            );
                        }
                        if (page === 2 || page === totalPages - 1) {
                            return (
                                <PaginationItem key={page}>
                                    <PaginationEllipsis className="h-8 w-8" />
                                </PaginationItem>
                            );
                        }
                        return null;
                    })}

                    <PaginationItem>
                        <PaginationNext 
                            onClick={() => onPageChange(currentPage + 1)}
                            className={`h-8 w-auto px-3 cursor-pointer ${currentPage === totalPages ? 'pointer-events-none opacity-50' : 'hover:bg-intellend-green/5'}`}
                        />
                    </PaginationItem>
                </PaginationContent>
            </Pagination>
        )}
    </div>
);

const JourneyAnalysis: React.FC<JourneyAnalysisProps> = ({ externalSettings, onSettingsChange, onOpenUrlAnalysis }) => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // View state
    const [view, setView] = useState<'list' | 'tracker'>('list');
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    // Detail modal state
    const [detailUser, setDetailUser] = useState<User | null>(null);

    // Leads by phone modal state
    const [leadsPhoneUser, setLeadsPhoneUser] = useState<User | null>(null);

    // CRM Notes modal state
    const [crmNotesUser, setCrmNotesUser] = useState<User | null>(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const pageSizeOptions = [5, 10, 25, 50, 100, 500];

    // Sorting state
    const [sortConfig, setSortConfig] = useState<{ key: keyof User; direction: 'asc' | 'desc' | null }>({
        key: 'id',
        direction: 'desc'
    });

    // Filtering state
    const [activeFilters, setActiveFilters] = useState<{
        bap_names: string[];
        is_staff: string;
        is_superuser: string;
        is_whitespace: string;
        search_query: string;
        full_name: string;
        business_name: string;
        journey_stage: string;
        updated_at_from: string;
        updated_at_to: string;
    }>({
        bap_names: [],
        is_staff: 'All',
        is_superuser: 'All',
        is_whitespace: 'All',
        search_query: '',
        full_name: '',
        business_name: '',
        journey_stage: '',
        updated_at_from: '',
        updated_at_to: '',
    });

    const [tempFilters, setTempFilters] = useState({ ...activeFilters });

    // Settings state
    const [settings, setSettings] = useState({
        timezone: externalSettings?.timezone || '+05:30',
        layout: (externalSettings?.layout || 'spacious') as 'compact' | 'standard' | 'spacious'
    });

    useEffect(() => {
        if (externalSettings) {
            setSettings(externalSettings);
        }
    }, [externalSettings]);

    const [showDownloadWarning, setShowDownloadWarning] = useState(false);
    const [showColumnSelection, setShowColumnSelection] = useState(false);
    const [pendingDownloadType, setPendingDownloadType] = useState<'csv' | 'journey_csv' | 'pdf' | null>(null);
    const [showMobileMenu, setShowMobileMenu] = useState(false);

    const JOURNEY_DATA_SECTIONS = [
        {
            id: 'user',
            label: 'User Info',
            fields: [
                { id: 'user|id', label: 'ID' },
                { id: 'user|username', label: 'Username' },
                { id: 'user|first_name', label: 'First Name' },
                { id: 'user|last_name', label: 'Last Name' },
                { id: 'user|email', label: 'Email' },
                { id: 'user|date_joined', label: 'Joined Date' },
            ]
        },
        {
            id: 'bap_info',
            label: 'BAP & Business Mapping',
            fields: [
                { id: 'bap_info|bap_name', label: 'BAP Name' },
                { id: 'bap_info|business_name', label: 'Business Name' },
                { id: 'bap_info|agent', label: 'Agent' },
                { id: 'bap_info|is_whitespace', label: 'Is Whitespace?' },
                { id: 'bap_info|anonymized_id', label: 'Anonymized ID' },
            ]
        },
        {
            id: 'individual_details',
            label: 'Personal Details',
            fields: [
                { id: 'individual_details|phone_number', label: 'Phone' },
                { id: 'individual_details|date_of_birth', label: 'DOB' },
                { id: 'individual_details|gender', label: 'Gender' },
                { id: 'individual_details|individual_pan_number', label: 'PAN' },
                { id: 'individual_details|residence_type', label: 'Residence' },
                { id: 'individual_details|residential_address', label: 'Res. Address' },
                { id: 'individual_details|communication_address', label: 'Comm. Address' },
            ]
        },
        {
            id: 'business_details',
            label: 'Business Details',
            fields: [
                { id: 'business_details|business_name', label: 'Biz Name' },
                { id: 'business_details|gst_number', label: 'GST' },
                { id: 'business_details|company_pan_number', label: 'Biz PAN' },
                { id: 'business_details|business_constitution_type', label: 'Constitution' },
                { id: 'business_details|date_of_incorporation', label: 'Incorp Date' },
                { id: 'business_details|business_vintage', label: 'Vintage' },
                { id: 'business_details|business_pincode', label: 'Pincode' },
            ]
        },
        {
            id: 'bank_details',
            label: 'Bank & Verification',
            fields: [
                { id: 'bank_details|bank_name', label: 'Bank Name' },
                { id: 'bank_details|account_number', label: 'Acc Number' },
                { id: 'bank_details|ifsc_code', label: 'IFSC' },
                { id: 'bank_verification|verified', label: 'Is Verified?' },
                { id: 'bank_verification|beneficiary_name_with_bank', label: 'Beneficiary Name' },
            ]
        },
        {
            id: 'loan_details',
            label: 'Loan & Journey',
            fields: [
                { id: 'loan_details|loan_amount', label: 'Amount' },
                { id: 'loan_details|loan_tenure', label: 'Tenure' },
                { id: 'loan_details|interest_rate', label: 'Rate' },
                { id: 'loan_details|repayment_method', label: 'Repayment Method' },
                { id: 'loan_details|repayment_type', label: 'Repayment Type' },
                { id: 'loan_details|fee_amount', label: 'Proc Fee' },
                { id: 'loan_details|lending_entity', label: 'Lender' },
                { id: 'journey|current_stage', label: 'Current Stage' },
                { id: 'journey|updated_at', label: 'Journey Updated At' },
            ]
        },
        {
            id: 'verification',
            label: 'PAN & GST Verification',
            fields: [
                { id: 'pan_verification|pan_no', label: 'PAN No' },
                { id: 'pan_verification|name', label: 'PAN Name' },
                { id: 'pan_verification|status', label: 'PAN Status' },
                { id: 'gst_verification|gst_no', label: 'GST No' },
                { id: 'gst_verification|company_name', label: 'GST Biz Name' },
            ]
        }
    ];

    const [selectedFields, setSelectedFields] = useState<Set<string>>(
        new Set(JOURNEY_DATA_SECTIONS.flatMap(s => s.fields.map(f => f.id)))
    );

    const uniqueBaps = Array.from(new Set(users.map(u => u.bap_name || "Empty BAP"))) as string[];

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoading(true);
                // exclude_staff_like: admin/staff accounts — and the customer-shaped
                // accounts they create when test-driving a journey with their own
                // phone number — must never appear in this analytics screen.
                const response = await axios.post("/api/public/users/", {
                    exclude_staff_like: true,
                });
                // Sort by ID in descending order
                const sortedUsers = (response.data as User[]).sort((a, b) => b.id - a.id);
                setUsers(sortedUsers);
                
                // Initialize filters with baps
                const baps = Array.from(new Set(sortedUsers.map(u => u.bap_name || "Empty BAP"))) as string[];
                setActiveFilters(prev => ({ ...prev, bap_names: baps }));
                setTempFilters(prev => ({ ...prev, bap_names: baps }));
                
                setError(null);
            } catch (err) {
                console.error("Error fetching users:", err);
                setError("Failed to load users. Please ensure you have admin permissions.");
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, []);

    // Filter and Sort Logic
    const filteredUsers = users.filter(user => {
        const userBap = user.bap_name || "Empty BAP";
        const matchesBap = activeFilters.bap_names.includes(userBap);
        const matchesAdmin = activeFilters.is_staff === 'All' || 
            (activeFilters.is_staff === 'Yes' ? user.is_staff : !user.is_staff);
        const matchesSuper = activeFilters.is_superuser === 'All' || 
            (activeFilters.is_superuser === 'Yes' ? user.is_superuser : !user.is_superuser);
        const matchesWhitespace = activeFilters.is_whitespace === 'All' || 
            (activeFilters.is_whitespace === 'Yes' ? user.is_whitespace : !user.is_whitespace);

        // Text Filters
        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
        const matchesFullName = !activeFilters.full_name || fullName.includes(activeFilters.full_name.toLowerCase());
        
        const bName = (user.business_name || '').toLowerCase();
        const matchesBusinessName = !activeFilters.business_name || bName.includes(activeFilters.business_name.toLowerCase());
        
        const phone = (user.phone_number || '').toLowerCase();
        const matchesSearch = !activeFilters.search_query || (
            fullName.includes(activeFilters.search_query.toLowerCase()) ||
            bName.includes(activeFilters.search_query.toLowerCase()) ||
            phone.includes(activeFilters.search_query.toLowerCase())
        );
        
        // Filter by journey_stage_name — the human-readable name derived from
        // checkpoint_index via _STAGE_NAME_MAP in the backend (e.g. "Personal Information",
        // "AA Consent"). This works for both dynamic (numeric page ID) and legacy journeys.
        // journey_stage (JourneyStage display label) is unreliable for dynamic journeys
        // because numeric page IDs fall through to the raw value.
        const stage = (user.journey_stage_name || '').toLowerCase();
        const matchesStage = !activeFilters.journey_stage || stage === activeFilters.journey_stage.toLowerCase();

        // Date Filters
        let matchesDate = true;
        if (activeFilters.updated_at_from || activeFilters.updated_at_to) {
            if (!user.journey_updated_at) {
                matchesDate = false;
            } else {
                const updatedDateObj = new Date(user.journey_updated_at);
                updatedDateObj.setHours(0, 0, 0, 0); // Normalize to start of day for comparison

                if (activeFilters.updated_at_from) {
                    const fromDate = new Date(activeFilters.updated_at_from);
                    fromDate.setHours(0, 0, 0, 0);
                    if (updatedDateObj < fromDate) matchesDate = false;
                }
                if (activeFilters.updated_at_to) {
                    const toDate = new Date(activeFilters.updated_at_to);
                    toDate.setHours(23, 59, 59, 999); // End of day for "to" comparison
                    if (updatedDateObj > toDate) matchesDate = false;
                }
            }
        }

        return matchesBap && matchesAdmin && matchesSuper && matchesWhitespace && 
               matchesFullName && matchesBusinessName && matchesSearch && matchesStage && matchesDate;
    });

    const isFiltered = 
        activeFilters.bap_names.length !== uniqueBaps.length || 
        activeFilters.is_staff !== 'All' || 
        activeFilters.is_superuser !== 'All' || 
        activeFilters.is_whitespace !== 'All' ||
        activeFilters.search_query !== '' ||
        activeFilters.full_name !== '' || 
        activeFilters.business_name !== '' || 
        activeFilters.journey_stage !== '' || 
        activeFilters.updated_at_from !== '' || 
        activeFilters.updated_at_to !== '';

    const sortedUsers = [...filteredUsers].sort((a, b) => {
        if (!sortConfig.key || !sortConfig.direction) return 0;
        
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === bValue) return 0;
        
        const modifier = sortConfig.direction === 'asc' ? 1 : -1;

        if (aValue === null) return 1;
        if (bValue === null) return -1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return aValue.localeCompare(bValue) * modifier;
        }

        return (aValue < bValue ? -1 : 1) * modifier;
    });

    const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedUsers = sortedUsers.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (key: keyof User) => {
        setSortConfig(prev => {
            if (prev.key === key) {
                if (prev.direction === 'asc') return { key, direction: 'desc' };
                if (prev.direction === 'desc') return { key: 'id', direction: 'desc' };
            }
            return { key, direction: 'asc' };
        });
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleItemsPerPageChange = (value: string) => {
        setItemsPerPage(Number(value));
        setCurrentPage(1);
    };

    const handleViewDetails = (user: User) => {
        setSelectedUser(user);
        setView('tracker');
    };

    const handleBackToList = () => {
        setView('list');
        setSelectedUser(null);
    };

    const handleNextUser = () => {
        if (!selectedUser) return;
        const currentIndex = sortedUsers.findIndex(u => u.id === selectedUser.id);
        const nextIndex = (currentIndex + 1) % sortedUsers.length;
        setSelectedUser(sortedUsers[nextIndex]);
    };

    const handlePrevUser = () => {
        if (!selectedUser) return;
        const currentIndex = sortedUsers.findIndex(u => u.id === selectedUser.id);
        const prevIndex = (currentIndex - 1 + sortedUsers.length) % sortedUsers.length;
        setSelectedUser(sortedUsers[prevIndex]);
    };

    const applyFilters = () => {
        setActiveFilters({ ...tempFilters });
        setCurrentPage(1);
    };

    const resetFilters = () => {
        const reset = { 
            bap_names: uniqueBaps, 
            is_staff: 'All', 
            is_superuser: 'All', 
            is_whitespace: 'All',
            search_query: '',
            full_name: '',
            business_name: '',
            journey_stage: '',
            updated_at_from: '',
            updated_at_to: ''
        };
        setTempFilters(reset);
        setActiveFilters(reset);
        setCurrentPage(1);
    };

    const toggleBap = (bap: string) => {
        setTempFilters(prev => {
            const isSelected = prev.bap_names.includes(bap);
            if (isSelected) {
                return { ...prev, bap_names: prev.bap_names.filter(b => b !== bap) };
            } else {
                return { ...prev, bap_names: [...prev.bap_names, bap] };
            }
        });
    };

    const toggleSelectAllBaps = () => {
        setTempFilters(prev => {
            const allSelected = prev.bap_names.length === uniqueBaps.length;
            if (allSelected) {
                return { ...prev, bap_names: [] };
            }
            return { ...prev, bap_names: uniqueBaps };
        });
    };

    const handleDownloadPDF = () => {
        const doc = new jsPDF('landscape');
        
        doc.setFontSize(16);
        doc.text("Journey Analysis Report", 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleString('en-GB', { timeZone: 'UTC' })} (UTC) | TZ: ${settings.timezone}`, 14, 22);
        
        const tableData = sortedUsers.map(user => [
            user.id.toString(),
            (user.first_name || user.last_name) ? `${user.first_name} ${user.last_name}` : "-",
            user.business_name || "-",
            user.phone_number || "-",
            user.username,
            user.journey_stage_name || user.journey_stage || "-",
            `${user.journey_stage || "-"} (${user.journey_index || "0/0"})`,
            formatDate(user.journey_updated_at, settings.timezone),
            user.bap_name || "-",
            user.is_whitespace ? "Yes" : "No",
            user.is_staff ? "Yes" : "No",
            user.is_superuser ? "Yes" : "No"
        ]);

        autoTable(doc, {
            head: [['ID', 'Full Name', 'Business', 'Phone', 'Username', 'Stage Name', 'Stage (Progress)', 'Last Updated', 'BAP', 'W.S.', 'Admin', 'S.User']],
            body: tableData,
            startY: 28,
            styles: {
                fontSize: 8,
                cellPadding: 3,
                overflow: 'linebreak',
                lineColor: [200, 200, 200], // Prominent borders for all cells
                lineWidth: 0.1
            },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                lineColor: [37, 99, 235],
                lineWidth: 0.5,
                fontStyle: 'bold'
            },
            columnStyles: {
                0: { cellWidth: 15, halign: 'center' },
                3: { halign: 'center' },
                5: { cellWidth: 28 }, // Stage Name
                6: { cellWidth: 28, halign: 'center' }, // Stage (Progress)
                7: { cellWidth: 30, halign: 'center' }, // Last Updated
                9: { halign: 'center' },
                10: { halign: 'center' },
                11: { halign: 'center' }
            },
            alternateRowStyles: {
                fillColor: [249, 250, 251]
            },
            // This ensures if you add 10+ columns later, the table splits horizontally
            // onto new pages instead of squishing and wrapping everything infinitely.
            horizontalPageBreak: true,
            horizontalPageBreakRepeat: 1 // Repeats the ID column on horizontal overflow pages
        });

        doc.save("journey_analysis_report.pdf");
    };

    const handleDownloadCSV = () => {
        const headers = ['ID', 'Full Name', 'Business', 'Phone', 'Username', 'Stage Name', 'Stage (Progress)', 'Last Updated', 'BAP', 'W.S.', 'Admin', 'S.User'];
        
        const csvRows = [
            headers.join(','),
            ...sortedUsers.map(user => {
                const name = (user.first_name || user.last_name) ? `${user.first_name} ${user.last_name}` : "-";
                // Escape quotes and handle commas in text fields
                const dateString = formatDate(user.journey_updated_at, settings.timezone).replace(/"/g, '""');
                return [
                    user.id,
                    `"${name.replace(/"/g, '""')}"`,
                    `"${(user.business_name || "-").replace(/"/g, '""')}"`,
                    `"${(user.phone_number || "-").replace(/"/g, '""')}"`,
                    `"${user.username.replace(/"/g, '""')}"`,
                    `"${(user.journey_stage_name || user.journey_stage || "-").replace(/"/g, '""')}"`,
                    `"${(user.journey_stage || "-").replace(/"/g, '""')} (${user.journey_index || "0/0"})"`,
                    `"${dateString}"`,
                    `"${(user.bap_name || "-").replace(/"/g, '""')}"`,
                    user.is_whitespace ? "Yes" : "No",
                    user.is_staff ? "Yes" : "No",
                    user.is_superuser ? "Yes" : "No"
                ].join(',');
            })
        ];

        const csvContent = csvRows.join('\n');
        const bom = '\uFEFF';
        const sanitized = csvContent.replace(/₹\s*/g, '');
        const blob = new Blob([bom + sanitized], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        
        link.setAttribute("href", url);
        link.setAttribute("download", "journey_analysis_report.csv");
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadUserDataCSV = async () => {
        try {
            const userIds = sortedUsers.map(u => u.id);
            if (userIds.length === 0) return;
            
            const response = await axios.post("/api/public/bulk_user_data_csv/", { 
                user_ids: userIds,
                selected_columns: Array.from(selectedFields),
                timezone: settings.timezone
            }, {
                responseType: 'blob'
            });
            
            const bom = '\uFEFF';
            const csvText = await response.data.text();
            const cleanedText = csvText.replace(/₹\s*/g, '');
            const blobOut = new Blob([bom + cleanedText], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blobOut);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `bulk_user_data_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            setShowColumnSelection(false);
            setPendingDownloadType(null);
        } catch (err) {
            console.error("Error downloading bulk user data:", err);
            alert("Failed to download detailed user data. Please try again.");
        }
    };

    const handleDownloadInit = (type: 'csv' | 'journey_csv' | 'pdf') => {
        if (isFiltered) {
            setPendingDownloadType(type);
            setShowDownloadWarning(true);
        } else {
            // Direct path if no filters active
            if (type === 'journey_csv') {
                setShowColumnSelection(true);
            } else if (type === 'csv') {
                handleDownloadCSV();
            } else if (type === 'pdf') {
                handleDownloadPDF();
            }
        }
    };

    const confirmDownload = () => {
        if (pendingDownloadType === 'journey_csv') {
            setShowDownloadWarning(false);
            setShowColumnSelection(true);
            // Don't clear type yet, we need it for ColumnSelectionDialog
        } else {
            if (pendingDownloadType === 'csv') handleDownloadCSV();
            else if (pendingDownloadType === 'pdf') handleDownloadPDF();
            setShowDownloadWarning(false);
            setPendingDownloadType(null);
        }
    };

    const toggleField = (fieldId: string) => {
        setSelectedFields(prev => {
            const next = new Set(prev);
            if (next.has(fieldId)) {
                next.delete(fieldId);
            } else {
                next.add(fieldId);
            }
            return next;
        });
    };

    const toggleSection = (sectionId: string) => {
        const section = JOURNEY_DATA_SECTIONS.find(s => s.id === sectionId);
        if (!section) return;

        const sectionFieldIds = section.fields.map(f => f.id);
        const allSelected = sectionFieldIds.every(id => selectedFields.has(id));

        setSelectedFields(prev => {
            const next = new Set(prev);
            if (allSelected) {
                // Deselect all in section
                sectionFieldIds.forEach(id => {
                    next.delete(id);
                });
            } else {
                // Select all in section
                sectionFieldIds.forEach(id => next.add(id));
            }
            return next;
        });
    };

    const isSectionSelected = (sectionId: string) => {
        const section = JOURNEY_DATA_SECTIONS.find(s => s.id === sectionId);
        return section?.fields.every(f => selectedFields.has(f.id)) ?? false;
    };

    const isSectionPartiallySelected = (sectionId: string) => {
        const section = JOURNEY_DATA_SECTIONS.find(s => s.id === sectionId);
        if (!section) return false;
        const selectedCount = section.fields.filter(f => selectedFields.has(f.id)).length;
        return selectedCount > 0 && selectedCount < section.fields.length;
    };

    const SortIcon = ({ column }: { column: keyof User }) => {
        if (sortConfig.key !== column) return <ArrowUpDown className="h-3.5 w-3.5 text-gray-300 ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity" />;
        return sortConfig.direction === 'asc' 
            ? <ArrowUp className="h-3.5 w-3.5 text-intellend-green ml-1.5" /> 
            : <ArrowDown className="h-3.5 w-3.5 text-intellend-green ml-1.5" />;
    };

    const TrackerView = () => {
        if (!selectedUser) return null;

        return (
            <div className="space-y-6">
                {/* Carousel Header */}
                <div className="flex items-center justify-between bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-2">
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={handlePrevUser}
                        className="shrink-0 h-10 w-10 rounded-full shadow-md border-gray-200 bg-white hover:bg-intellend-green hover:text-white hover:border-intellend-green transition-all duration-300 group"
                    >
                        <ChevronLeft className="h-6 w-6 text-gray-600 group-hover:text-white" />
                    </Button>

                    <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6 px-4 sm:px-8 py-2.5 bg-intellend-green/5 rounded-2xl border border-intellend-green/20 shadow-inner flex-1 min-w-0 justify-center mx-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-intellend-green/10 rounded-lg">
                                <UserCheck className="h-5 w-5 text-intellend-green" />
                            </div>
                            <div className="flex flex-col">
                                <h3 className="text-sm sm:text-lg font-bold text-gray-900 truncate flex items-center gap-2">
                                    <span>{selectedUser.first_name} {selectedUser.last_name}</span>
                                    {selectedUser.business_name && <span className="text-gray-400 font-normal">({selectedUser.business_name})</span>}
                                </h3>
                            </div>
                        </div>
                        <div className="hidden sm:block h-8 w-[1px] bg-intellend-green/20" />
                        <div className="flex items-center gap-4">
                            <div className="flex flex-col items-center">
                                <span className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">Stage</span>
                                <span className="text-xs font-semibold text-intellend-green">{selectedUser.journey_stage || "N/A"}</span>
                            </div>
                            <div className="flex flex-col items-center">
                                <span className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">ID</span>
                                <span className="text-xs font-mono font-semibold text-gray-600">#{selectedUser.id}</span>
                            </div>
                        </div>
                    </div>

                    <Button
                        variant="outline"
                        size="icon"
                        onClick={handleNextUser}
                        className="shrink-0 h-10 w-10 rounded-full shadow-md border-gray-200 bg-white hover:bg-intellend-green hover:text-white hover:border-intellend-green transition-all duration-300 group"
                    >
                        <ChevronRight className="h-6 w-6 text-gray-600 group-hover:text-white" />
                    </Button>
                </div>

                {/* Verification Tracker Component */}
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <VerificationDebug isEmbedded={true} forcedUserId={String(selectedUser.id)} />
                </div>
            </div>
        );
    };

    return (
        <>

            <Card className="border-intellend-green/10 shadow-sm overflow-hidden">
                <CardHeader className="flex flex-col gap-3 p-4 pb-2.5 border-b bg-gray-50/30">
                    <div className="flex items-center justify-between w-full">
                        {/* Title Row */}
                        <div className="flex items-center gap-3">
                            <CardTitle className="text-xl font-semibold">
                                {view === 'list' ? 'User Journeys' : 'Journey Analysis'}
                            </CardTitle>
                            
                            {/* Total Users Badge - visible on mobile and desktop in list view */}
                            {view === 'list' && (
                                <div className="hidden xs:flex items-center gap-1.5 px-3 py-1 bg-intellend-green/10 rounded-full">
                                    <span className="text-xs font-semibold text-intellend-green">Total:</span>
                                    <span className="text-sm font-bold text-gray-900">{users.length}</span>
                                </div>
                            )}
                        </div>
                        
                        {/* Controls Row */}
                        <div className="flex items-center gap-2">
                        {/* Back Button - tracker view */}
                        {view === 'tracker' && (
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={handleBackToList}
                                className="flex items-center justify-center gap-2 h-9 px-4 bg-white hover:bg-gray-50 rounded-lg transition-all border border-gray-200 shadow-sm text-gray-700 hover:text-intellend-green font-semibold text-sm"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                <span className="hidden xs:inline">Back to User List</span>
                                <span className="xs:hidden">Back</span>
                            </Button>
                        )}
                            
                            {/* Mobile Menu Toggle Button */}
                            {view === 'list' && (
                                <button
                                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                                    className="md:hidden flex items-center justify-center h-9 w-9 bg-white hover:bg-gray-50 rounded-lg transition-all border border-gray-200 shadow-sm text-gray-700 hover:text-intellend-green font-semibold"
                                    title="Menu"
                                >
                                    <svg className={`h-5 w-5 transition-transform ${showMobileMenu ? 'rotate-45' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                    
                    {/* Desktop Menu Row */}
                    {view === 'list' && (
                        <div className="hidden md:flex items-center gap-3">
                            <style>{filterGlowStyles}</style>
                            <style>{layoutStyles}</style>
                            <button
                                onClick={() => handleDownloadInit('journey_csv')}
                                className="hidden flex items-center justify-center gap-2 h-9 px-4 bg-white hover:bg-gray-50 rounded-lg transition-all border border-gray-200 shadow-sm text-gray-700 hover:text-intellend-green font-semibold text-sm w-full md:w-auto"
                                title="User data for all the users filtered below"
                            >
                                <Zap className="h-4 w-4 text-orange-500" />
                                <span>Journey Data CSV</span>
                            </button>
                            <button
                                onClick={() => handleDownloadInit('csv')}
                                className="flex items-center justify-center gap-2 h-9 px-4 bg-white hover:bg-gray-50 rounded-lg transition-all border border-gray-200 shadow-sm text-gray-700 hover:text-intellend-green font-semibold text-sm w-full md:w-auto"
                                title="download below table as CSV"
                            >
                                <Download className="h-4 w-4" />
                                <span>CSV</span>
                            </button>
                            <button
                                onClick={() => handleDownloadInit('pdf')}
                                className="flex items-center justify-center gap-2 h-9 px-4 bg-white hover:bg-gray-50 rounded-lg transition-all border border-gray-200 shadow-sm text-gray-700 hover:text-intellend-green font-semibold text-sm w-full md:w-auto"
                                title="download below table as PDF"
                            >
                                <Download className="h-4 w-4" />
                                <span>PDF</span>
                            </button>
                            <Dialog onOpenChange={(open) => {
                                if (!open) {
                                    setTempFilters({ ...activeFilters });
                                }
                            }}>
                                <DialogTrigger asChild>
                                    <button 
                                        className={`flex items-center justify-center gap-2 h-9 px-4 bg-white hover:bg-gray-50 rounded-lg transition-all border border-gray-200 shadow-sm text-gray-700 hover:text-intellend-green font-semibold text-sm w-full md:w-auto ${isFiltered ? 'filter-active-glow' : ''}`}
                                        title="Filter"
                                    >
                                        <Filter className={`h-4 w-4 ${isFiltered ? 'text-intellend-green' : ''}`} />
                                        <span>Filter</span>
                                        {isFiltered && (
                                            <span className="ml-1 px-1.5 py-0.5 bg-intellend-green text-white text-[10px] rounded-full scale-110 shadow-sm">
                                                {activeFilters.bap_names.length < uniqueBaps.length ? activeFilters.bap_names.length : '!'}
                                            </span>
                                        )}
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="w-[95vw] h-[90vh] max-w-none flex flex-col p-0 overflow-hidden border-none shadow-2xl">
                                    <DialogHeader className="p-6 border-b flex flex-row items-center justify-between space-y-0 bg-gray-50/50">
                                        <div className="flex-1">
                                            <DialogTitle className="text-xl md:text-2xl font-bold flex items-center gap-2">
                                                <Filter className="h-5 md:h-6 w-5 md:w-6 text-intellend-green" />
                                                Advanced Filters
                                            </DialogTitle>
                                            <span className="text-xs text-gray-400 italic flex items-center gap-1.5 mt-1 ml-1 hidden md:flex">
                                                * Changes are applied only when you click "Apply and Close"
                                            </span>
                                        </div>
                                        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-4 w-full md:w-auto md:pr-8 md:border-l md:pl-4">
                                            <Button 
                                                variant="outline" 
                                                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300 font-bold shadow-sm px-4 md:px-6 h-10 text-sm order-2 md:order-1"
                                                onClick={resetFilters}
                                            >
                                                Reset All Filters
                                            </Button>
                                            <div className="flex items-center gap-2 h-full order-1 md:order-2">
                                                <DialogClose asChild>
                                                    <Button 
                                                        className="bg-intellend-green hover:bg-intellend-green/90 text-white font-semibold px-4 md:px-6 h-10 text-sm flex-1 md:flex-initial"
                                                        disabled={tempFilters.bap_names.length === 0}
                                                        onClick={applyFilters}
                                                    >
                                                        <Check className="h-4 w-4 mr-2 hidden md:inline" />
                                                        Apply and Close
                                                    </Button>
                                                </DialogClose>
                                                <DialogClose asChild>
                                                    <Button 
                                                        variant="outline"
                                                        className="border-gray-200 hover:bg-gray-100 font-medium px-4 md:px-6 h-10 text-sm flex-1 md:flex-initial"
                                                        onClick={() => setTempFilters({ ...activeFilters })}
                                                    >
                                                        Close
                                                    </Button>
                                                </DialogClose>
                                            </div>
                                        </div>
                                    </DialogHeader>
                                    <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-white">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-8 items-start">
                                            {/* BAP Name Filter */}
                                            <div className="space-y-4 bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                                                <div className="flex items-center justify-between border-b pb-3 mb-2">
                                                    <label className="text-sm font-black text-[#0f172a] uppercase tracking-widest flex items-center gap-2">
                                                        <CheckSquare className="h-4 w-4 text-intellend-green" />
                                                        BAP Names
                                                    </label>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        className="h-8 text-[11px] font-bold uppercase tracking-tighter text-intellend-green hover:bg-intellend-green/10"
                                                        onClick={toggleSelectAllBaps}
                                                    >
                                                        {tempFilters.bap_names.length === uniqueBaps.length ? "Deselect All" : "Select All"}
                                                    </Button>
                                                </div>
                                                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                                                    {uniqueBaps.map(bap => (
                                                        <div key={bap} className="flex items-center space-x-3 group cursor-pointer" onClick={() => toggleBap(bap)}>
                                                            <Checkbox 
                                                                id={`bap-${bap}`}
                                                                checked={tempFilters.bap_names.includes(bap)}
                                                                className="data-[state=checked]:bg-intellend-green data-[state=checked]:border-intellend-green transition-all"
                                                            />
                                                            <Label 
                                                                htmlFor={`bap-${bap}`}
                                                                className="text-sm font-medium text-gray-600 group-hover:text-intellend-green transition-colors cursor-pointer"
                                                            >
                                                                {bap}
                                                            </Label>
                                                        </div>
                                                    ))}
                                                </div>
                                                {tempFilters.bap_names.length === 0 && (
                                                    <p className="text-[10px] text-red-500 font-bold italic animate-pulse">
                                                        * At least one BAP must be selected to apply filters
                                                    </p>
                                                )}
                                            </div>

                                            {/* Admin Status Filter */}
                                            <div className="space-y-4 bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                                                <label className="text-sm font-black text-[#0f172a] uppercase tracking-widest border-b pb-3 flex items-center gap-2">
                                                    <Info className="h-4 w-4 text-intellend-green" />
                                                    Admin Status
                                                </label>
                                                <Select 
                                                    value={tempFilters.is_staff} 
                                                    onValueChange={(val) => setTempFilters(prev => ({ ...prev, is_staff: val }))}
                                                >
                                                    <SelectTrigger className="w-full bg-white border-gray-200">
                                                        <SelectValue placeholder="Any Admin Status" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="All">All Roles</SelectItem>
                                                        <SelectItem value="Yes">Admins Only</SelectItem>
                                                        <SelectItem value="No">Non-Admins</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            {/* Superuser Status Filter */}
                                            <div className="space-y-4 bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                                                <label className="text-sm font-black text-[#0f172a] uppercase tracking-widest border-b pb-3 flex items-center gap-2">
                                                    <Info className="h-4 w-4 text-intellend-green" />
                                                    S.User Status
                                                </label>
                                                <Select 
                                                    value={tempFilters.is_superuser} 
                                                    onValueChange={(val) => setTempFilters(prev => ({ ...prev, is_superuser: val }))}
                                                >
                                                    <SelectTrigger className="w-full bg-white border-gray-200">
                                                        <SelectValue placeholder="Any S.User Status" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="All">All Users</SelectItem>
                                                        <SelectItem value="Yes">Superusers Only</SelectItem>
                                                        <SelectItem value="No">Non-Superusers</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            {/* Whitespace Status Filter */}
                                            <div className="space-y-4 bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                                                <label className="text-sm font-black text-[#0f172a] uppercase tracking-widest border-b pb-3 flex items-center gap-2">
                                                    <Info className="h-4 w-4 text-intellend-green" />
                                                    Whitespace
                                                </label>
                                                <Select 
                                                    value={tempFilters.is_whitespace} 
                                                    onValueChange={(val) => setTempFilters(prev => ({ ...prev, is_whitespace: val }))}
                                                >
                                                    <SelectTrigger className="w-full bg-white border-gray-200">
                                                        <SelectValue placeholder="Any Whitespace Status" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="All">All Users</SelectItem>
                                                        <SelectItem value="Yes">Whitespace Only</SelectItem>
                                                        <SelectItem value="No">Non-Whitespace</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            {/* Global Search Filter */}
                                            <div className="space-y-4 bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                                                <label className="text-sm font-black text-[#0f172a] uppercase tracking-widest border-b pb-3 flex items-center gap-2">
                                                    <Search className="h-4 w-4 text-intellend-green" />
                                                    Search
                                                </label>
                                                <Input 
                                                    placeholder="Search name, business or phone..." 
                                                    value={tempFilters.search_query} 
                                                    onChange={(e) => setTempFilters(prev => ({ ...prev, search_query: e.target.value }))}
                                                    className="w-full bg-white border-gray-200"
                                                />
                                            </div>

                                            {/* Full Name Filter */}
                                            <div className="space-y-4 bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                                                <label className="text-sm font-black text-[#0f172a] uppercase tracking-widest border-b pb-3 flex items-center gap-2">
                                                    <Info className="h-4 w-4 text-intellend-green" />
                                                    Full Name
                                                </label>
                                                <Input 
                                                    placeholder="Search by full name..." 
                                                    value={tempFilters.full_name} 
                                                    onChange={(e) => setTempFilters(prev => ({ ...prev, full_name: e.target.value }))}
                                                    className="w-full bg-white border-gray-200"
                                                />
                                            </div>

                                            {/* Business Name Filter */}
                                            <div className="space-y-4 bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                                                <label className="text-sm font-black text-[#0f172a] uppercase tracking-widest border-b pb-3 flex items-center gap-2">
                                                    <Info className="h-4 w-4 text-intellend-green" />
                                                    Business Name
                                                </label>
                                                <Input 
                                                    placeholder="Search by business name..." 
                                                    value={tempFilters.business_name} 
                                                    onChange={(e) => setTempFilters(prev => ({ ...prev, business_name: e.target.value }))}
                                                    className="w-full bg-white border-gray-200"
                                                />
                                            </div>

                                            {/* Stage Filter — hardcoded Caprion/PhonePe journey stages 1-19.
                                                journey_stage in the API response is the JourneyStage display label
                                                (dict(JourneyStage.choices).get(state.current_stage, state.current_stage)).
                                                The filter comparison is case-insensitive (.toLowerCase() on both sides). */}
                                            <div className="space-y-4 bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                                                <label className="text-sm font-black text-[#0f172a] uppercase tracking-widest border-b pb-3 flex items-center gap-2">
                                                    <Info className="h-4 w-4 text-intellend-green" />
                                                    Journey Stage
                                                </label>
                                                <Select
                                                    value={tempFilters.journey_stage || "__all__"}
                                                    onValueChange={(val) => setTempFilters(prev => ({ ...prev, journey_stage: val === "__all__" ? "" : val }))}
                                                >
                                                    <SelectTrigger className="w-full bg-white border-gray-200">
                                                        <SelectValue placeholder="All Stages" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="__all__">All Stages</SelectItem>
                                                        {/* Values are the exact journey_stage_name strings from
                                                            _STAGE_NAME_MAP in apis.py (keyed by checkpoint_index+1).
                                                            These work for both dynamic (Caprion) and legacy journeys. */}
                                                        {[
                                                            // Pre-BRE
                                                            { n:  1, label: "Personal Information" },
                                                            { n:  2, label: "Business Information" },
                                                            { n:  3, label: "Initial Verification" },
                                                            { n:  4, label: "Lender Offers" },
                                                            // BRE
                                                            { n:  5, label: "Bureau Verification" },
                                                            { n:  6, label: "AA Consent" },
                                                            { n:  7, label: "AA Verification" },
                                                            // Post-BRE
                                                            { n:  8, label: "Customize Loan" },
                                                            { n:  9, label: "Final Loan Offer" },
                                                            // KYC
                                                            { n: 10, label: "Selfie Photo Capture" },
                                                            { n: 11, label: "Aadhaar Verification" },
                                                            { n: 12, label: "Identity Verification" },
                                                            { n: 13, label: "Bank Details" },
                                                            { n: 14, label: "Digio eMandate Setup" },
                                                            { n: 15, label: "Final Verification" },
                                                            // Agreement
                                                            { n: 16, label: "Agreement PDF Display" },
                                                            { n: 17, label: "Agreement Signing" },
                                                            { n: 18, label: "Dealer Approval Validation" },
                                                            { n: 19, label: "Loan Agreement Status" },
                                                        ].map(({ n, label }) => (
                                                            <SelectItem key={label} value={label.toLowerCase()}>
                                                                {`${n}. ${label}`}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            {/* Last Updated Date Filter */}
                                            <div className="space-y-4 bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                                                <label className="text-sm font-black text-[#0f172a] uppercase tracking-widest border-b pb-3 flex items-center gap-2">
                                                    <Info className="h-4 w-4 text-intellend-green" />
                                                    Last Updated
                                                </label>
                                                <div className="flex flex-col gap-3">
                                                    <div>
                                                        <span className="text-xs text-gray-500 font-semibold mb-1 block">From</span>
                                                        <Input 
                                                            type="date"
                                                            value={tempFilters.updated_at_from} 
                                                            onChange={(e) => setTempFilters(prev => ({ ...prev, updated_at_from: e.target.value }))}
                                                            className="w-full bg-white border-gray-200"
                                                        />
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-gray-500 font-semibold mb-1 block">To</span>
                                                        <Input 
                                                            type="date"
                                                            value={tempFilters.updated_at_to} 
                                                            onChange={(e) => setTempFilters(prev => ({ ...prev, updated_at_to: e.target.value }))}
                                                            className="w-full bg-white border-gray-200"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                         </div>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>
                    )}

                    {/* Mobile Menu - Expanded State */}
                    {showMobileMenu && (
                        <div className="md:hidden flex flex-col gap-2 w-full mt-3 pt-3 border-t border-gray-200">
                            <style>{filterGlowStyles}</style>
                            <style>{layoutStyles}</style>
                            <button
                                onClick={() => {
                                    handleDownloadInit('journey_csv');
                                    setShowMobileMenu(false);
                                }}
                                className="hidden flex items-center justify-center gap-2 h-9 px-4 bg-white hover:bg-gray-50 rounded-lg transition-all border border-gray-200 shadow-sm text-gray-700 hover:text-intellend-green font-semibold text-sm w-full"
                                title="User data for all the users filtered below"
                            >
                                <Zap className="h-4 w-4 text-orange-500" />
                                <span>Journey Data CSV</span>
                            </button>
                            <button
                                onClick={() => {
                                    handleDownloadInit('csv');
                                    setShowMobileMenu(false);
                                }}
                                className="flex items-center justify-center gap-2 h-9 px-4 bg-white hover:bg-gray-50 rounded-lg transition-all border border-gray-200 shadow-sm text-gray-700 hover:text-intellend-green font-semibold text-sm w-full"
                                title="download below table as CSV"
                            >
                                <Download className="h-4 w-4" />
                                <span>CSV</span>
                            </button>
                            <button
                                onClick={() => {
                                    handleDownloadInit('pdf');
                                    setShowMobileMenu(false);
                                }}
                                className="flex items-center justify-center gap-2 h-9 px-4 bg-white hover:bg-gray-50 rounded-lg transition-all border border-gray-200 shadow-sm text-gray-700 hover:text-intellend-green font-semibold text-sm w-full"
                                title="download below table as PDF"
                            >
                                <Download className="h-4 w-4" />
                                <span>PDF</span>
                            </button>
                            <Dialog onOpenChange={(open) => {
                                if (!open) {
                                    setTempFilters({ ...activeFilters });
                                }
                            }}>
                                <DialogTrigger asChild>
                                    <button 
                                        className={`flex items-center justify-center gap-2 h-9 px-4 bg-white hover:bg-gray-50 rounded-lg transition-all border border-gray-200 shadow-sm text-gray-700 hover:text-intellend-green font-semibold text-sm w-full ${isFiltered ? 'filter-active-glow' : ''}`}
                                        title="Filter"
                                    >
                                        <Filter className={`h-4 w-4 ${isFiltered ? 'text-intellend-green' : ''}`} />
                                        <span>Filter</span>
                                        {isFiltered && (
                                            <span className="ml-1 px-1.5 py-0.5 bg-intellend-green text-white text-[10px] rounded-full scale-110 shadow-sm">
                                                {activeFilters.bap_names.length < uniqueBaps.length ? activeFilters.bap_names.length : '!'}
                                            </span>
                                        )}
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="w-[95vw] md:w-full h-[90vh] max-w-4xl flex flex-col p-0 overflow-hidden border-none shadow-2xl">
                                    <DialogHeader className="p-4 md:p-6 border-b flex flex-col md:flex-row items-start md:items-center justify-between space-y-3 md:space-y-0 bg-gray-50/50">
                                        <div className="flex-1">
                                            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                                                <Filter className="h-6 w-6 text-intellend-green" />
                                                Advanced Filters
                                            </DialogTitle>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button 
                                                variant="outline" 
                                                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300 font-bold shadow-sm px-6 h-10"
                                                onClick={resetFilters}
                                            >
                                                Reset All Filters
                                            </Button>
                                            <DialogClose asChild>
                                                <Button 
                                                    className="bg-intellend-green hover:bg-intellend-green/90 text-white font-semibold px-6 h-10"
                                                    disabled={tempFilters.bap_names.length === 0}
                                                    onClick={applyFilters}
                                                >
                                                    Apply and Close
                                                </Button>
                                            </DialogClose>
                                        </div>
                                    </DialogHeader>
                                </DialogContent>
                            </Dialog>
                        </div>
                    )}
                </CardHeader>
                <CardContent className="p-0">
                    {/* Download Warning Dialog */}
                    <AlertDialog open={showDownloadWarning} onOpenChange={setShowDownloadWarning}>
                        <AlertDialogContent className="bg-white rounded-2xl border-none shadow-2xl max-w-md">
                            <AlertDialogHeader>
                                <AlertDialogTitle className="text-xl font-bold flex items-center gap-2 text-gray-900">
                                    <Info className="h-5 w-5 text-amber-500" />
                                    Export Warning
                                </AlertDialogTitle>
                                <AlertDialogDescription className="text-gray-600 font-medium py-2">
                                    You have active filters. The exported file will <span className="text-intellend-green font-bold">only include</span> the {filteredUsers.length} records that match your current selection.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="gap-2">
                                <AlertDialogCancel className="rounded-xl border-gray-100 hover:bg-gray-50 font-semibold px-6">
                                    Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction 
                                    onClick={confirmDownload}
                                    className="bg-intellend-green hover:bg-intellend-green/90 text-white rounded-xl font-semibold px-6 shadow-lg shadow-intellend-green/20"
                                >
                                    Download Now
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>

                    {/* Column Selection Dialog */}
                    <Dialog open={showColumnSelection} onOpenChange={setShowColumnSelection}>
                        <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden border-none shadow-2xl rounded-3xl">
                            <DialogHeader className="p-8 border-b bg-gray-50/50 flex flex-row items-center justify-between space-y-0">
                                <div className="space-y-1">
                                    <DialogTitle className="text-2xl font-bold flex items-center gap-3 text-gray-900">
                                        <div className="p-2 bg-intellend-green/10 rounded-xl">
                                            <CheckSquare className="h-6 w-6 text-intellend-green" />
                                        </div>
                                        Select CSV Columns
                                    </DialogTitle>
                                    <p className="text-sm text-gray-500 font-medium ml-11">
                                        Choose the data points you want to include in your export
                                    </p>
                                </div>
                                <div className="flex items-center gap-4 pr-12">
                                    <Button 
                                        variant="outline" 
                                        className="rounded-xl font-semibold border-gray-200 hover:bg-gray-100"
                                        onClick={() => {
                                            setShowColumnSelection(false);
                                            setPendingDownloadType(null);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        className="bg-intellend-green hover:bg-intellend-green/90 text-white rounded-xl font-bold px-8 shadow-lg shadow-intellend-green/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={handleDownloadUserDataCSV}
                                        disabled={selectedFields.size === 0}
                                    >
                                        Download CSV
                                    </Button>
                                </div>
                            </DialogHeader>
                            
                            <div className="flex-1 overflow-y-auto p-8 bg-white">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {JOURNEY_DATA_SECTIONS.map(section => (
                                        <div key={section.id} className="group flex flex-col bg-gray-50/50 rounded-2xl border border-gray-100 overflow-hidden hover:border-intellend-green/30 hover:bg-white transition-all duration-300">
                                            <div 
                                                className="p-4 border-b flex items-center justify-between cursor-pointer hover:bg-intellend-green/5 transition-colors"
                                                onClick={() => toggleSection(section.id)}
                                            >
                                                <span className="text-sm font-black text-gray-900 uppercase tracking-wider">{section.label}</span>
                                                <Checkbox 
                                                    checked={isSectionSelected(section.id) ? true : isSectionPartiallySelected(section.id) ? "indeterminate" : false}
                                                    className="data-[state=checked]:bg-intellend-green data-[state=checked]:border-intellend-green rounded-md"
                                                />
                                            </div>
                                            <div className="p-4 space-y-3">
                                                {section.fields.map(field => (
                                                    <div 
                                                        key={field.id} 
                                                        className="flex items-center justify-between group/item cursor-pointer"
                                                        onClick={() => toggleField(field.id)}
                                                    >
                                                        <Label className="text-[13px] font-medium text-gray-600 group-hover/item:text-intellend-green transition-colors cursor-pointer">
                                                            {field.label}
                                                        </Label>
                                                        <Checkbox 
                                                            checked={selectedFields.has(field.id)}
                                                            className="data-[state=checked]:bg-intellend-green/80 data-[state=checked]:border-intellend-green/80 rounded-sm scale-90"
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="p-6 bg-gray-50 border-t flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                                    <Info className="h-4 w-4 text-intellend-green" />
                                    <span>{selectedFields.size} data points selected for export</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="text-xs font-bold uppercase tracking-tight text-gray-400 hover:text-red-500"
                                        onClick={() => setSelectedFields(new Set())}
                                    >
                                        Deselect All
                                    </Button>
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="text-xs font-bold uppercase tracking-tight text-intellend-green hover:bg-intellend-green/5"
                                        onClick={() => setSelectedFields(new Set(JOURNEY_DATA_SECTIONS.flatMap(s => s.fields.map(f => f.id))))}
                                    >
                                        Select All
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>

                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <div className="relative">
                                <Loader2 className="h-10 w-10 text-intellend-green animate-spin" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="h-2 w-2 bg-intellend-green rounded-full" />
                                </div>
                            </div>
                            <p className="text-gray-500 mt-4 animate-pulse">Fetching users...</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12 bg-red-50 rounded-lg border border-red-100">
                            <p className="text-red-600 font-semibold">{error}</p>
                        </div>
                    ) : users.length === 0 ? (
                        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-dashed">
                            No users found.
                        </div>
                    ) : view === 'list' ? (
                        <div className="space-y-3 animate-in fade-in duration-500">
                            <div className="border-t border-b overflow-hidden isolate relative bg-white">
                                <Table className={`text-[13px] table-layout-${settings.layout}`}>
                                    <TableHeader className="bg-gray-50/80">
                                        <TableRow className="hover:bg-transparent border-b">
                                            <TableHead className="w-[80px] py-2 px-3 group cursor-pointer" onClick={() => handleSort('id')}>
                                                <div className="flex items-center font-bold text-gray-700">
                                                    ID <SortIcon column="id" />
                                                </div>
                                            </TableHead>
                                            <TableHead className="min-w-[180px] py-2 px-3 group cursor-pointer" onClick={() => handleSort('first_name')}>
                                                <div className="flex items-center font-bold text-gray-700">
                                                    Full Name <SortIcon column="first_name" />
                                                </div>
                                            </TableHead>
                                            <TableHead className="min-w-[160px] py-2 px-3 group cursor-pointer" onClick={() => handleSort('business_name')}>
                                                <div className="flex items-center font-bold text-gray-700">
                                                    Business Name <SortIcon column="business_name" />
                                                </div>
                                            </TableHead>
                                            <TableHead className="w-[140px] py-2 px-3 group cursor-pointer" onClick={() => handleSort('phone_number')}>
                                                <div className="flex items-center font-bold text-gray-700">
                                                    Phone <SortIcon column="phone_number" />
                                                </div>
                                            </TableHead>
                                            <TableHead className="w-[150px] py-2 px-3 group cursor-pointer" onClick={() => handleSort('username')}>
                                                <div className="flex items-center font-bold text-gray-700">
                                                    Username <SortIcon column="username" />
                                                </div>
                                            </TableHead>
                                            <TableHead className="min-w-[140px] py-1 px-3 group cursor-pointer" onClick={() => handleSort('journey_stage_name')}>
                                                <div className="flex flex-col justify-center">
                                                    <div className="flex items-center font-bold text-gray-700">
                                                        Stage Name <SortIcon column="journey_stage_name" />
                                                    </div>
                                                </div>
                                            </TableHead>
                                            <TableHead className="min-w-[110px] py-1 px-3 group cursor-pointer" onClick={() => handleSort('journey_stage')}>
                                                <div className="flex flex-col justify-center">
                                                    <div className="flex items-center font-bold text-gray-700">
                                                        Stage <SortIcon column="journey_stage" />
                                                    </div>
                                                    <span className="text-[10px] text-gray-400 font-normal leading-none mt-0.5">Progress</span>
                                                </div>
                                            </TableHead>
                                             <TableHead className="w-[140px] py-2 px-3 group cursor-pointer" onClick={() => handleSort('journey_updated_at')}>
                                                 <div className="flex items-center font-bold text-gray-700">
                                                     Last Updated <SortIcon column="journey_updated_at" />
                                                 </div>
                                             </TableHead>
                                            <TableHead className="w-[120px] py-2 px-3 group cursor-pointer" onClick={() => handleSort('bap_name')}>
                                                <div className="flex items-center font-bold text-gray-700">
                                                    BAP Name <SortIcon column="bap_name" />
                                                </div>
                                            </TableHead>
                                            <TableHead className="w-[40px] py-2 px-1 text-center group cursor-pointer" onClick={() => handleSort('is_whitespace')}>
                                                <div className="flex w-full items-center justify-center font-bold text-gray-700">
                                                    W.S <SortIcon column="is_whitespace" />
                                                </div>
                                            </TableHead>
                                            <TableHead className="w-[40px] py-2 px-1 text-center group cursor-pointer" onClick={() => handleSort('is_staff')}>
                                                <div className="flex w-full items-center justify-center font-bold text-gray-700">
                                                    Admin <SortIcon column="is_staff" />
                                                </div>
                                            </TableHead>
                                            <TableHead className="w-[50px] py-2 px-1 text-center group cursor-pointer" onClick={() => handleSort('is_superuser')}>
                                                <div className="flex w-full items-center justify-center font-bold text-gray-700">
                                                    S.User <SortIcon column="is_superuser" />
                                                </div>
                                            </TableHead>
                                            <TableHead className="w-[160px] py-2 px-3 text-center font-bold text-gray-700">Action</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {paginatedUsers.map((user) => (
                                            <TableRow key={user.id} className="hover:bg-intellend-green/5 transition-all duration-200 cursor-default group border-b last:border-0 h-10">
                                                <TableCell className="w-[80px] py-1 px-3 font-mono text-[11px] text-gray-500">
                                                    #{user.id}
                                                </TableCell>
                                                <TableCell className="min-w-[180px] py-1 px-3 text-gray-700 font-medium leading-tight">
                                                    {user.first_name || user.last_name ? `${user.first_name} ${user.last_name}` : <span className="text-gray-300 italic">No name provided</span>}
                                                </TableCell>
                                                <TableCell className="min-w-[180px] py-1 px-3 text-gray-700 leading-tight">
                                                    {user.business_name || <span className="text-gray-300 italic">-</span>}
                                                </TableCell>
                                                <TableCell className="w-[140px] py-1 px-3 font-medium">
                                                    {user.phone_number ? (
                                                        <span className={user.phone_from_individual ? 'text-gray-700' : 'text-red-500'} title={user.phone_from_individual ? undefined : 'Phone from lead mapping (no IndividualDetail)'}>
                                                            {user.phone_number}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-300 italic">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="w-[150px] py-1 px-3">
                                                    <div className="flex items-center gap-1">
                                                        <span className="font-mono text-xs text-gray-600 bg-gray-100/80 px-2 py-1 rounded border border-gray-200" title={user.username}>
                                                            {user.username.length > 6 
                                                                ? `...${user.username.slice(-6)}` 
                                                                : user.username}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="min-w-[140px] py-1 px-3 text-gray-700">
                                                    <span className="font-medium text-xs text-gray-800">
                                                        {user.journey_stage_name && user.journey_stage_name !== '-'
                                                            ? user.journey_stage_name
                                                            : (user.journey_stage || "-")}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="min-w-[110px] py-1 px-3 text-gray-700">
                                                    <div className="flex flex-col leading-[1.1]">
                                                        <span className="font-semibold text-xs">{user.journey_stage || "-"}</span>
                                                        <span className="text-[10px] text-gray-400 font-mono italic">
                                                            {user.journey_index || "0/0"}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                 <TableCell className="w-[140px] py-1 px-3 text-gray-700 text-[10px] whitespace-nowrap">
                                                     {formatDate(user.journey_updated_at, settings.timezone)}
                                                 </TableCell>
                                                <TableCell className="w-[120px] py-1 px-3 text-gray-700 text-xs">
                                                    <div className="flex flex-col gap-0.5 leading-tight">
                                                        <span>{user.bap_name || <span className="text-gray-300 italic">-</span>}</span>
                                                        {user.data_source && (
                                                            <span
                                                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-blue-50 text-blue-600 border border-blue-100 w-fit"
                                                                title={`Source: ${user.data_source}`}
                                                            >
                                                                🔗 {user.data_source}
                                                            </span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="w-[40px] py-1 px-1 align-middle">
                                                    <div className="mx-auto w-fit flex items-center justify-center origin-center scale-[0.65] group-hover:scale-75 transition-transform">
                                                        {user.is_whitespace ? <GreenCheck /> : <RedX />}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="w-[40px] py-1 px-1 align-middle">
                                                    <div className="mx-auto w-fit flex items-center justify-center origin-center scale-[0.65] group-hover:scale-75 transition-transform">
                                                        {user.is_staff ? <GreenCheck /> : <RedX />}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="w-[50px] py-1 px-1 align-middle">
                                                    <div className="mx-auto w-fit flex items-center justify-center origin-center scale-[0.65] group-hover:scale-75 transition-transform">
                                                        {user.is_superuser ? <GreenCheck /> : <RedX />}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="w-[160px] py-1 px-3 text-center">
                                                    <div className="flex items-center justify-center gap-1">
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            className="h-7 w-7 rounded-md text-blue-600 border-blue-600/20 hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                                                            onClick={() => setDetailUser(user)}
                                                            title="View Details"
                                                        >
                                                            <Info className="h-3.5 w-3.5" />
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            className="h-7 w-7 rounded-md text-intellend-green border-intellend-green/20 hover:bg-intellend-green hover:text-white transition-all shadow-sm"
                                                            onClick={() => handleViewDetails(user)}
                                                            title="Verifications"
                                                        >
                                                            <Eye className="h-3.5 w-3.5" />
                                                        </Button>
                                                        <Button
                                                             variant="outline"
                                                             size="icon"
                                                             className="h-7 w-7 rounded-md text-orange-500 border-orange-400/20 hover:bg-orange-500 hover:text-white transition-all shadow-sm"
                                                             onClick={() => setLeadsPhoneUser(user)}
                                                             title="View CRM Leads"
                                                         >
                                                             <ArrowLeftRight className="h-3.5 w-3.5" />
                                                         </Button>
                                                        <Button
                                                             variant="outline"
                                                             size="icon"
                                                             className="h-7 w-7 rounded-md text-purple-600 border-purple-400/20 hover:bg-purple-600 hover:text-white transition-all shadow-sm"
                                                             onClick={() => setCrmNotesUser(user)}
                                                             title="View CRM Notes"
                                                             disabled={!user.crm_lead_id}
                                                         >
                                                             <StickyNote className="h-3.5 w-3.5" />
                                                         </Button>
                                                        {onOpenUrlAnalysis && (
                                                            <Button
                                                                 variant="outline"
                                                                 size="icon"
                                                                 className="h-7 w-7 rounded-md text-cyan-600 border-cyan-500/20 hover:bg-cyan-600 hover:text-white transition-all shadow-sm"
                                                                 onClick={() => onOpenUrlAnalysis(user)}
                                                                 title="URL / Time Analysis"
                                                             >
                                                                 <RouteIcon className="h-3.5 w-3.5" />
                                                             </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            {/* Pagination Controls */}
                            <PaginationControls 
                                currentPage={currentPage}
                                itemsPerPage={itemsPerPage}
                                totalPages={totalPages}
                                totalItems={users.length}
                                filteredCount={filteredUsers.length}
                                startIndex={startIndex}
                                pageSizeOptions={pageSizeOptions}
                                onItemsPerPageChange={handleItemsPerPageChange}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    ) : (
                        <TrackerView />
                    )}
                </CardContent>
            </Card>

            {/* CRM Notes Modal */}
            <CRMLeadNotesModal
                leadId={crmNotesUser?.crm_lead_id ?? null}
                leadName={crmNotesUser ? `${crmNotesUser.first_name} ${crmNotesUser.last_name}`.trim() || crmNotesUser.username : undefined}
                isOpen={!!crmNotesUser}
                onClose={() => setCrmNotesUser(null)}
            />

            {/* Leads by Phone Modal */}
            <LeadsByPhoneModal
                phone={leadsPhoneUser?.phone_number ?? null}
                userName={leadsPhoneUser ? `${leadsPhoneUser.first_name} ${leadsPhoneUser.last_name}`.trim() || leadsPhoneUser.username : undefined}
                currentLeadId={leadsPhoneUser?.crm_lead_id ?? null}
                userId={leadsPhoneUser?.id ?? null}
                isOpen={!!leadsPhoneUser}
                onClose={() => setLeadsPhoneUser(null)}
                onCrmLeadChanged={(newCrmLeadId) => {
                    if (!leadsPhoneUser) return;
                    // Update the user in the local list so the table and CRM Notes button reflect the new link immediately
                    setUsers(prev => prev.map(u =>
                        u.id === leadsPhoneUser.id
                            ? { ...u, crm_lead_id: newCrmLeadId }
                            : u
                    ));
                    // Also keep the modal's own user reference in sync so Unlink works correctly
                    setLeadsPhoneUser(prev => prev ? { ...prev, crm_lead_id: newCrmLeadId } : prev);
                }}
            />

            {/* View Details Modal */}
            <UserDetailModal
                userId={detailUser?.id ?? null}
                username={detailUser?.username}
                firstName={detailUser?.first_name}
                lastName={detailUser?.last_name}
                businessName={detailUser?.business_name}
                journeyIndex={detailUser?.journey_index}
                isOpen={!!detailUser}
                timezone={settings.timezone}
                onClose={() => setDetailUser(null)}
            />
        </>
    );
};

export default JourneyAnalysis;
